import {
  ChatItem,
  MessageSource,
  ToolCallStatus,
} from "features/assistant/definitions";

export interface ToolConfirmationRequest {
  requestId: string;
  toolName: string;
  description: string;
}

export type FeedbackValue = "THUMBS_UP" | "THUMBS_DOWN";

export interface ChatState {
  items: ChatItem[];
  isConnected: boolean;
  isLoading: boolean;
  isLoadingHistory: boolean;
  error: string | null;
  inlineError: string | null;
  pendingConfirmation: ToolConfirmationRequest | null;
  pendingMessageId: string | null;
  // Source declared by the in-flight TEXT_START; applied to the message that
  // APPEND_DELTA lazily creates for pendingMessageId.
  pendingSource: MessageSource;
  sessionFeedback: FeedbackValue | null;
}

export type ChatAction =
  | { type: "SET_CONNECTED"; connected: boolean }
  | { type: "SET_LOADING"; loading: boolean }
  | {
      type: "ADD_MESSAGE";
      id: string;
      role: "user" | "assistant";
      content: string;
      isStreaming: boolean;
      displayContent?: string;
    }
  | { type: "APPEND_DELTA"; messageId: string; delta: string }
  | { type: "MARK_COMPLETE"; messageId: string }
  | { type: "CLEAR_MESSAGES" }
  | { type: "SET_ERROR"; error: string | null }
  | {
      type: "SET_PENDING_CONFIRMATION";
      confirmation: ToolConfirmationRequest | null;
    }
  | { type: "SET_ITEMS"; items: ChatItem[] }
  | { type: "SET_LOADING_HISTORY"; loading: boolean }
  | { type: "RESET" }
  | {
      type: "ADD_TOOL_CALL";
      toolUseId: string;
      toolName: string;
    }
  | {
      type: "COMPLETE_TOOL_CALL";
      toolUseId: string;
      status: "completed" | "error";
    }
  | {
      type: "SET_PENDING_MESSAGE_ID";
      messageId: string | null;
      source?: MessageSource;
    }
  | { type: "SET_INLINE_ERROR"; message: string | null }
  | { type: "SET_SESSION_FEEDBACK"; feedback: FeedbackValue | null };

const sealStreamingMessage = (items: ChatItem[]): ChatItem[] => {
  const last = items[items.length - 1];
  if (last?.type === "message" && last.isStreaming) {
    return [...items.slice(0, -1), { ...last, isStreaming: false }];
  }
  return items;
};

export const chatReducer = (
  state: ChatState,
  action: ChatAction,
): ChatState => {
  switch (action.type) {
    case "SET_CONNECTED":
      return {
        ...state,
        isConnected: action.connected,
        ...(action.connected
          ? {}
          : {
              items: [],
              isLoading: false,
              pendingConfirmation: null,
              pendingMessageId: null,
            }),
      };
    case "SET_LOADING":
      return { ...state, isLoading: action.loading };
    case "ADD_MESSAGE":
      return {
        ...state,
        items: [
          ...state.items,
          {
            type: "message",
            id: action.id,
            role: action.role,
            content: action.content,
            isStreaming: action.isStreaming,
            displayContent: action.displayContent,
          },
        ],
      };
    case "APPEND_DELTA": {
      const last = state.items[state.items.length - 1];
      if (
        last?.type === "message" &&
        last.id.startsWith(action.messageId) &&
        last.isStreaming
      ) {
        return {
          ...state,
          items: [
            ...state.items.slice(0, -1),
            { ...last, content: last.content + action.delta },
          ],
        };
      }
      if (state.pendingMessageId === action.messageId) {
        const seq = state.items.filter(
          (item) =>
            item.type === "message" && item.id.startsWith(action.messageId),
        ).length;
        const id = seq === 0 ? action.messageId : `${action.messageId}-${seq}`;
        return {
          ...state,
          items: [
            ...state.items,
            {
              type: "message",
              id,
              role: "assistant",
              content: action.delta,
              isStreaming: true,
              source: state.pendingSource,
            },
          ],
        };
      }
      return state;
    }
    case "MARK_COMPLETE":
      return {
        ...state,
        pendingMessageId: null,
        items: state.items.map((item) =>
          item.type === "message" && item.id.startsWith(action.messageId)
            ? { ...item, isStreaming: false }
            : item,
        ),
      };
    case "CLEAR_MESSAGES":
      return {
        ...state,
        items: [],
        pendingConfirmation: null,
        pendingMessageId: null,
      };
    case "SET_ERROR":
      return { ...state, error: action.error };
    case "SET_PENDING_CONFIRMATION":
      return { ...state, pendingConfirmation: action.confirmation };
    case "SET_ITEMS":
      return {
        ...state,
        items: action.items,
        isLoadingHistory: false,
        error: null,
        pendingMessageId: null,
      };
    case "SET_LOADING_HISTORY":
      return { ...state, isLoadingHistory: action.loading };
    case "RESET":
      return initialChatState;
    case "ADD_TOOL_CALL":
      return {
        ...state,
        items: [
          ...sealStreamingMessage(state.items),
          {
            type: "tool_call",
            id: crypto.randomUUID(),
            toolUseId: action.toolUseId,
            toolName: action.toolName,
            status: "running" as ToolCallStatus,
          },
        ],
      };
    case "COMPLETE_TOOL_CALL":
      return {
        ...state,
        items: state.items.map((item) =>
          item.type === "tool_call" && item.toolUseId === action.toolUseId
            ? { ...item, status: action.status as ToolCallStatus }
            : item,
        ),
      };
    case "SET_PENDING_MESSAGE_ID":
      return {
        ...state,
        pendingMessageId: action.messageId,
        pendingSource: action.source ?? "assistant",
      };
    case "SET_INLINE_ERROR":
      return { ...state, inlineError: action.message };
    case "SET_SESSION_FEEDBACK":
      return { ...state, sessionFeedback: action.feedback };
    default:
      return state;
  }
};

export const initialChatState: ChatState = {
  items: [],
  isConnected: false,
  isLoading: false,
  isLoadingHistory: false,
  error: null,
  inlineError: null,
  pendingConfirmation: null,
  pendingMessageId: null,
  pendingSource: "assistant",
  sessionFeedback: null,
};
